import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import type { Dispatch } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  BlackbaudRaisersEdgeIntegration as BREIntegration,
  IBlackbaudContactMapping,
} from '@feathr/blackbox';
import { CardV2 as Card, Spinner, toast, Toggle } from '@feathr/components';

import SyncMappingCard from '../../SyncMappingCard';
import DeleteMappingButton from '../DeleteMappingButton';
import type { TMappingDisabledReason } from '../DeleteMappingButton/';
import RaisersEdgeDateLastSynced from '../RaisersEdgeDateLastSynced';
import SyncPersonSelect from './SyncPersonSelect';

import * as styles from './RaisersEdgeSyncPerson.css';

interface IRaisersEdgeSyncPersonProps {
  addMappingButton: JSX.Element;
  integration: BREIntegration;
  setHasMappings: Dispatch<React.SetStateAction<boolean>>;
}

function RaisersEdgeSyncPerson({
  addMappingButton,
  integration,
  setHasMappings,
}: IRaisersEdgeSyncPersonProps): JSX.Element {
  const { t } = useTranslation();
  const [mappings, setMappings] = useState<IBlackbaudContactMapping[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    async function getMappings(): Promise<void> {
      // TODO: Refactor to use ContactMappings.list() as part of #2651.
      try {
        setIsLoading(true);
        const mappings = await integration.getContactMappings();
        setIsLoading(false);
        setMappings(mappings);
        setHasMappings(true);
      } catch (error) {
        setIsLoading(false);
        toast(t("Couldn't load mappings. {{error}}", { error }), { type: 'error' });
      }
    }

    if (!mappings) {
      getMappings();
    }
  }, []);

  const downsyncActivities = integration.get('downsync_activities');

  async function handleToggleConstituentSync(syncValue: boolean): Promise<void> {
    integration.set({
      downsync_activities: { ...downsyncActivities, constituents: syncValue },
    });
    try {
      await integration.patchDirty();
      toast(t(syncValue ? 'Enabled constituent sync.' : 'Disabled constituent sync.'), {
        type: 'success',
      });
    } catch (error) {
      toast(t("Couldn't update setting. {{error}}", { error }), {
        type: 'error',
      });
    }
  }

  const noMappingsMessage: JSX.Element = (
    <div className={styles.message}>
      <h4>{t('No mappings found')}</h4>
      <p>{t('Add a new mapping to get started.')}</p>
      {addMappingButton}
    </div>
  );

  // Determine the reason a mapping is disabled.
  const getDisabledReason = (
    mapping: IBlackbaudContactMapping,
  ): TMappingDisabledReason | undefined => {
    // If a mapping is required, it cannot be deleted.
    if (mapping.required) {
      return 'required';
    }
    // If a mapping is being provisioned, it cannot be deleted.
    if (mapping.has_provisioning_subscriptions) {
      return 'provisioning';
    }
    return undefined;
  };

  const constituentSyncCard: JSX.Element = (
    <Card contentClassName={styles.syncCard} name={'constituent-sync-card'}>
      <Card.Content contentClassName={styles.syncCardContent}>
        <div className={styles.syncCardTextContent}>
          <div className={styles.syncCardTitle}>
            {t("Sync constituents from Raiser's Edge NXT to Feathr")}
          </div>
          <div className={styles.syncCardDescription}>
            {t(
              'When enabled, constituent create/update events in RE NXT trigger person record updates in Feathr according to mapped fields.',
            )}
          </div>
        </div>
        <Toggle
          onChange={(): void => {
            handleToggleConstituentSync(!downsyncActivities?.constituents);
          }}
          value={downsyncActivities?.constituents ?? false}
        />
      </Card.Content>
    </Card>
  );

  if (isLoading) {
    return <Spinner />;
  } else {
    if (mappings) {
      // We want to sort the required mappings to the top of the list.
      const mappingCards: JSX.Element[] = mappings
        .sort((a, b) => {
          // TODO: Implement sorting in the query as part of #2991.
          return a.required === b.required ? 0 : a.required ? -1 : 1;
        })
        .map((mapping) => (
          <SyncMappingCard
            controls={[
              <RaisersEdgeDateLastSynced
                className={styles.sync}
                dateLastSynced={mapping.date_last_synced}
                key={`${mapping.id}-date-last-synced`}
                showLastSyncedPrefix={false}
                showNeverSynced={true}
              />,
              <DeleteMappingButton
                disabled={mapping.required || mapping.has_provisioning_subscriptions}
                disabledReason={getDisabledReason(mapping)}
                id={mapping.id}
                integration={integration}
                key={`${mapping.id}-delete-mapping-button`}
              />,
            ]}
            key={mapping.id}
            mapping={mapping}
            mappings={mappings}
            target={
              <SyncPersonSelect
                className={styles.select}
                integration={integration}
                mapping={mapping}
                mappings={mappings}
              />
            }
          />
        ));
      return (
        <>
          {constituentSyncCard}
          {mappingCards}
        </>
      );
    }
    return (
      <>
        {constituentSyncCard}
        {noMappingsMessage}
      </>
    );
  }
}

export default observer(RaisersEdgeSyncPerson);
